import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { BreadcrumbSchema } from '@/components/feature/SEOHead';
import { articles } from '@/mocks/movies';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 星辰影院" />
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-3">文章未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: '电影资讯', url: '/category/kehuan' },
    { name: article.title, url: `/article/${article.slug}` },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.date,
        publisher: {
          '@type': 'Organization',
          name: '星辰影院',
          url: 'https://www.xingchencinema.com/',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.xingchencinema.com/article/${article.slug}`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 星辰影院`}
        description={article.excerpt}
        keywords={`星辰影院,${article.category},电影资讯,影评,观影指南`}
        canonicalUrl={`https://www.xingchencinema.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.coverImage}
        schemaData={articleSchema}
      />

      <BreadcrumbSchema items={breadcrumbItems} />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-6 md:py-8" aria-label="面包屑导航">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500 overflow-x-auto" aria-label="Breadcrumb">
              {breadcrumbItems.map((item, idx) => (
                <span key={idx} className="flex items-center gap-1 whitespace-nowrap">
                  {idx > 0 && (
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </span>
                  )}
                  {idx < breadcrumbItems.length - 1 ? (
                    <Link to={item.url} className="hover:text-primary-500 transition-colors">
                      {item.name}
                    </Link>
                  ) : (
                    <span className="text-foreground-800 font-medium">{item.name}</span>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </section>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 text-xs text-foreground-500">
                <span className="bg-accent-100 text-accent-900 px-2 py-0.5 rounded-md font-medium">
                  {article.category}
                </span>
                <span>{article.date}</span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-eye-line text-[10px]"></i>
                  </span>
                  {article.views.toLocaleString()} 次阅读
                </span>
                <span>{article.author}</span>
              </div>
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed">{article.excerpt}</p>
            </header>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:mb-4 [&_p]:text-sm [&_p]:md:text-base [&_p]:leading-7 [&_p]:md:leading-8
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70" aria-label="相关文章">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <article key={rel.id} className="group cursor-pointer">
                      <Link to={`/article/${rel.slug}`} className="block">
                        <div className="aspect-[16/9] rounded-lg overflow-hidden mb-3">
                          <img
                            src={rel.coverImage}
                            alt={rel.title}
                            title={rel.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {rel.title}
                        </h3>
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-12 pt-8 border-t border-background-200/70 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                返回首页
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}